import React, { useState, useEffect, useRef } from 'react';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import AudioVisualization from './components/AudioVisualization';
import PatientInfo from './components/PatientInfo';
import PatientConstantes from './components/PatientConstantes';
import ControlsPanel from './components/ControlsPanel';
import WelcomeDialog from './components/WelcomeDialog';
import LoadingScreen from './components/LoadingScreen';
import EditReport from './components/EditReport';
import HomePage from './components/HomePage';
import ConsultationHistory from './components/ConsultationHistory';
import ReportViewer from './components/ReportViewer';
import { useWebSocket } from './hooks/useWebSocket';
import { useAudioRecording } from './hooks/useAudioRecording';
import { useTranscription } from './hooks/useTranscription';
import { useSummary } from './hooks/useSummary';
import { useDarkMode } from './hooks/useDarkMode';
import { useAuth } from './contexts/AuthContext';
import { getWebSocketUrl, env, logEnvConfig } from './config/env';
import './App.css';

const WS_URL = getWebSocketUrl();

function App() {
  // Get user from auth context for tenantId
  const { user } = useAuth();

  // Core state
  const [showWelcome, setShowWelcome] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    // Skip welcome dialog in test mode
    if (urlParams.get('test') === 'edit-report') {
      return false;
    }
    return !localStorage.getItem('doctorInfo');
  });
  const [doctorInfo, setDoctorInfo] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    // Provide sample doctor info in test mode
    if (urlParams.get('test') === 'edit-report') {
      return {
        name: 'Dr. Sarah Martin',
        specialty: 'Médecine générale',
        place: 'Lumière Medical Office',
        consultationType: 'Consultation de suivi'
      };
    }
    try {
      return JSON.parse(localStorage.getItem('doctorInfo')) || {};
    } catch (e) {
      return {};
    }
  });
  
  // Navigation state - check for test mode
  const [currentView, setCurrentView] = useState(() => {
    // Check for ?test=edit-report in URL to directly access EditReport
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('test') === 'edit-report') {
      return 'editReport';
    }
    // Start with home page by default
    return 'home';
  }); // 'home', 'transcription', 'editReport', 'final', 'history'

  // Consultation history state
  const [selectedConsultationId, setSelectedConsultationId] = useState(null);
  // Track where user came from when entering EditReport
  const [editReportSource, setEditReportSource] = useState('transcription');
  // Key to force PatientInfo re-mount when loading patient from history
  const [patientInfoKey, setPatientInfoKey] = useState(0);
  // Track if the current patient is from an existing record (for update feature)
  const [isExistingPatient, setIsExistingPatient] = useState(false);
  // Patient constantes state
  const [patientConstantes, setPatientConstantes] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    // Provide sample patient constantes in test mode
    if (urlParams.get('test') === 'edit-report') {
      return {
        temperature: '36.8',
        tension: '130/80',
        frequenceCardiaque: '72',
        saturation: '98',
        glycemie: '1.1',
        poids: '75',
        taille: '175'
      };
    }
    return {
      temperature: '',
      tension: '',
      frequenceCardiaque: '',
      saturation: '',
      glycemie: '',
      poids: '',
      taille: ''
    };
  });

  // Patient info state
  const [patientInfo, setPatientInfo] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    // Provide sample patient info in test mode
    if (urlParams.get('test') === 'edit-report') {
      return {
        nom: 'Dupont',
        prenom: 'Marc',
        sexe: 'M',
        dateNaissance: '1978-03-15',
        adresse: '123 Rue de la République, 75011 Paris'
      };
    }
    return {
      nom: '',
      prenom: '',
      sexe: '',
      dateNaissance: '',
      adresse: ''
    };
  });

  const [reportData, setReportData] = useState(() => {
    // If in test mode, provide sample medical data with extracted facts
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('test') === 'edit-report') {
      return {
        extractedFacts: {
          consultation: {
            value: 'Consultation on September 18, 2025 at 2:30 PM in general medicine office',
            confidence: 0.95
          },
          patient: {
            value: 'Patient M. Dupont, 45 ans, masculin, 75kg, 175cm',
            confidence: 0.98
          },
          medecin: {
            value: 'Dr. Sarah Martin, General Practitioner, Lumière Medical Office',
            confidence: 0.99
          },
          motif_consultation: {
            value: 'Douleurs lombaires intenses avec irradiation sciatique droite, aggravation progressive depuis 1 semaine',
            confidence: 0.92
          },
          histoire_maladie: {
            value: 'Début progressif il y a 6 mois. Douleur de type mécanique, aggravée par les efforts et la position assise prolongée. Amélioration relative au repos. Pas de déficit moteur. Antécédent de lombalgie commune il y a 2 ans. Patient décrit une douleur irradiante dans la jambe droite jusqu\'au pied.',
            confidence: 0.88
          },
          antecedents: {
            value: 'Family history of hypertension. No surgical history. Known penicillin allergy. Current treatments: none.',
            confidence: 0.87
          },
          constantes: {
            value: 'TA: 130/80 mmHg, FC: 72 bpm, Température: 36.8°C, Poids: 75kg, Taille: 175cm, BMI: 24.5',
            confidence: 0.94,
            structured: {
              temperature: '36.8',
              tension: '130/80',
              frequenceCardiaque: '72',
              saturation: '98',
              glycemie: '1.1',
              poids: '75',
              taille: '175'
            }
          },
          examen_clinique: {
            value: 'Patient en bon état général. Examen neurologique: ROT conservés et symétriques, absence de déficit sensitivo-moteur, signe de Lasègue positif à 60° à droite. Palpation: contracture paravertébrale L4-L5-S1. Mobilité rachidienne limitée en flexion. Marche possible mais douloureuse.',
            confidence: 0.91
          },
          examens_complementaires: {
            value: 'Radiographies du rachis lombaire de face et profil réalisées: discopathie L5-S1 modérée, pas de spondylolisthésis. Bilan biologique non nécessaire à ce stade.',
            confidence: 0.90
          },
          ordonnance: {
            value: 'Relative rest 48-72h. Acetaminophen 1g x3/day for 7 days. Ibuprofen 400mg x3/day for 5 days if no contraindication. Physical therapy after acute phase (10 sessions). Sick leave 3 days.',
            confidence: 0.89
          },
          conclusion_traitement: {
            value: 'Lombo-sciatique commune droite L5-S1, probablement d\'origine disco-radiculaire. Absence de syndrome de la queue de cheval. Réévaluation dans 8-10 jours. Si pas d\'amélioration: envisager IRM lombaire et avis spécialisé.',
            confidence: 0.93
          }
        },
        originalText: 'Le patient s\'est présenté à la consultation avec une plainte principale de douleur intense au bas du dos...'
      };
    }
    return '';
  });
  
  // Refs
  const autoSummaryTimerRef = useRef(null);
  
  // UI state
  const [alert, setAlert] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const [logs, setLogs] = useState('');
  const [logsCollapsed, setLogsCollapsed] = useState(true);

  // Custom hooks
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { 
    displayText, 
    currentPartial, 
    isTyping, 
    processTranscriptText,
    finalText 
  } = useTranscription();
  
  const {
    micGranted,
    permission,
    isRecording,
    isPaused,
    status,
    isListening,
    hasSpoken,
    requestMic,
    startRecording,
    stopRecording,
    togglePause,
    drawWaveform,
    getRecordedAudio,
    clearRecordedAudio
  } = useAudioRecording(WS_URL, processTranscriptText);

  const {
    currentSummary,
    summaryVisible,
    setSummaryVisible,
    isSummaryLoading,
    generateReport,
    startAutoSummary,
    stopAutoSummary
  } = useSummary(finalText);

  // Logging function
  const logln = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => `${prev}[${timestamp}] ${message}\n`);
    console.log(message);
  };

  // Alert functions
  const showAlert = (msg) => {
    setAlert(msg);
    setTimeout(() => setAlert(''), 5000);
  };

  const hideAlert = () => setAlert('');

  // Loading screen functions
  const showLoadingScreen = () => {
    setIsLoading(true);
    setLoadingProgress(0);
  };

  const updateLoadingProgress = (progress, text) => {
    setLoadingProgress(progress);
    setLoadingText(text);
  };

  const hideLoadingScreen = () => {
    setIsLoading(false);
    setLoadingProgress(0);
    setLoadingText('');
  };

  // Welcome dialog handlers
  const handleWelcomeSubmit = (info) => {
    setDoctorInfo(info);
    localStorage.setItem('doctorInfo', JSON.stringify(info));
    setShowWelcome(false);
    logln(`Session started - Doctor: ${info.name}, Specialty: ${info.specialty}`);
  };

  const handleEditPersonalInfo = () => {
    setShowWelcome(true);
  };

  const handleWelcomeClose = () => {
    setShowWelcome(false);
  };

  // Navigation handlers
  const navigateToEditReport = (data = '') => {
    console.log('🔄 Navigating to EditReport...');

    // Check audio blob availability before navigation
    const audioBlob = getRecordedAudio();
    if (audioBlob) {
      console.log('✅ Audio blob available before navigation:', audioBlob.size, 'bytes');
    } else {
      console.warn('⚠️ No audio blob available before navigation!');
    }

    // Enhance report data with current patient constantes
    let enhancedData = data;
    if (typeof data === 'object' && data.extractedFacts) {
      enhancedData = {
        ...data,
        extractedFacts: {
          ...data.extractedFacts,
          constantes: {
            ...data.extractedFacts.constantes,
            structured: patientConstantes
          }
        }
      };
    }
    setReportData(enhancedData);
    setEditReportSource('transcription'); // Track that we came from transcription
    setCurrentView('editReport');
  };

  const navigateToTranscription = () => {
    setCurrentView('transcription');
  };

  const navigateToFinal = () => {
    setCurrentView('final');
  };

  const handleEditReportCancel = () => {
    // Navigate back based on where user came from
    if (editReportSource === 'history') {
      setCurrentView('history');
    } else {
      setCurrentView('transcription');
    }
  };

  const handleStartConsultation = () => {
    // Reset all patient-related state for a fresh consultation
    setPatientInfo({
      nom: '',
      prenom: '',
      sexe: '',
      dateNaissance: '',
      adresse: ''
    });
    setPatientInfoKey(prev => prev + 1); // Force PatientInfo to remount fresh
    setIsExistingPatient(false); // New consultation, not an existing patient
    setPatientConstantes({
      temperature: '',
      tension: '',
      frequenceCardiaque: '',
      saturation: '',
      glycemie: '',
      poids: '',
      taille: ''
    });
    setReportData('');
    setLogs('');
    setCurrentView('transcription');
  };

  const handleNavigateHome = () => {
    setCurrentView('home');
  };

  const handleViewHistory = () => {
    setCurrentView('history');
  };

  const handleNewConsultationWithPatient = (patientData) => {
    // Set patient info from history and navigate directly to transcription to start recording
    setPatientInfo(patientData);
    setPatientInfoKey(prev => prev + 1); // Force PatientInfo to remount with new data
    setIsExistingPatient(true); // This is an existing patient from history
    setCurrentView('transcription');
  };

  const handleViewReport = (consultationId) => {
    setSelectedConsultationId(consultationId);
  };

  const handleViewConsultationFromHome = (consultation) => {
    // Navigate to history view and open the report viewer for this consultation
    setCurrentView('history');
    setSelectedConsultationId(consultation.id);
  };

  const handleCloseReportViewer = () => {
    setSelectedConsultationId(null);
  };

  const handleEditConsultation = (reportData, patientInfo, consultationId) => {
    // Set the report data and patient info for EditReport
    setReportData(reportData);
    setPatientInfo(patientInfo);
    setSelectedConsultationId(null); // Close the viewer
    setEditReportSource('history'); // Track that we came from history
    setCurrentView('editReport'); // Switch to edit view
  };

  // Apply dark mode class
  useEffect(() => {
    const classes = [];
    if (darkMode) classes.push('dark');
    document.body.className = classes.join(' ');
  }, [darkMode, currentView]);

  // Auto-summary management - runs continuously during recording
  useEffect(() => {
    if (isRecording && finalText.length > env.MIN_TEXT_LENGTH_FOR_SUMMARY) {
      startAutoSummary();
      logln('Automatic summary system activated');
    } else if (!isRecording) {
      stopAutoSummary();
      logln('Automatic summary system paused');
    }
    
    return () => stopAutoSummary();
  }, [isRecording, finalText.length, startAutoSummary, stopAutoSummary]);

  // Initialize
  useEffect(() => {
    if (location.protocol === "file:") {
      showAlert("⚠️ Tu as ouvert cette page en file://. Lance go run ./cmd/api puis ouvre http://localhost:8080/");
    }

    // Log environment configuration in development
    logEnvConfig();

    if (doctorInfo.name) {
      logln(`Restored session - Doctor: ${doctorInfo.name}, Specialty: ${doctorInfo.specialty}, Place: ${doctorInfo.place}`);
    }
  }, []);

  return (
    <div className={`app-container ${currentView === 'home' ? 'home-view' : ''} ${currentView === 'history' ? 'history-view' : ''}`}>
      {/* Welcome Dialog */}
      {showWelcome && (
        <WelcomeDialog
          onSubmit={handleWelcomeSubmit}
          onClose={handleWelcomeClose}
          initialInfo={doctorInfo}
          isEditing={!!doctorInfo.name}
        />
      )}

      {/* Loading Screen */}
      {isLoading && (
        <LoadingScreen
          progress={loadingProgress}
          text={loadingText}
        />
      )}

      {/* Render different views based on currentView */}
      {currentView === 'home' ? (
        <HomePage
          onStartConsultation={handleStartConsultation}
          onViewHistory={handleViewHistory}
          onViewConsultation={handleViewConsultationFromHome}
          onNewConsultationWithPatient={handleNewConsultationWithPatient}
          doctorInfo={doctorInfo}
        />
      ) : currentView === 'history' ? (
        <>
          <ConsultationHistory
            onViewReport={handleViewReport}
            onBack={handleNavigateHome}
            onNewConsultation={handleNewConsultationWithPatient}
          />
          {selectedConsultationId && (
            <ReportViewer
              consultationId={selectedConsultationId}
              onClose={handleCloseReportViewer}
              onEdit={handleEditConsultation}
            />
          )}
        </>
      ) : currentView === 'editReport' ? (
        <EditReport
          reportData={reportData}
          patientInfo={patientInfo}
          patientConstantes={patientConstantes}
          getRecordedAudio={getRecordedAudio}
          clearRecordedAudio={clearRecordedAudio}
          isExistingConsultation={editReportSource === 'history'}
          onSave={(data) => {
            setReportData(data);
            logln('Report saved successfully');
            navigateToFinal();
          }}
          onCancel={handleEditReportCancel}
          onNavigateHome={handleNavigateHome}
        />
      ) : currentView === 'final' ? (
        <div className="final-page">
          <h1>Consultation terminée</h1>
          <p>Rapport sauvegardé avec succès</p>
          <button onClick={() => {
            setCurrentView('home');
            // Reset states for new consultation
            setReportData('');
            setLogs('');
            setPatientConstantes({
              temperature: '',
              tension: '',
              frequenceCardiaque: '',
              saturation: '',
              glycemie: '',
              poids: '',
              taille: ''
            });
            setPatientInfo({
              nom: '',
              prenom: '',
              sexe: '',
              dateNaissance: '',
              adresse: ''
            });
          }}>
            New consultation
          </button>
        </div>
      ) : (
        <>
          {/* Sidebar */}
          <Sidebar
            doctorInfo={doctorInfo}
            micGranted={micGranted}
            permission={permission}
            onEditPersonalInfo={handleEditPersonalInfo}
            isRecording={isRecording}
            isPaused={isPaused}
            onRequestMic={requestMic}
            onStartRecording={startRecording}
            onStopRecording={stopRecording}
            onTogglePause={togglePause}
            patientInfoValid={patientInfo.nom && patientInfo.prenom}
            onNavigateHome={handleNavigateHome}
          />

          {/* Main Content */}
          <div className="main-content">
            {/* Top Bar */}
            <TopBar
              status={status}
              isRecording={isRecording}
              isPaused={isPaused}
              darkMode={darkMode}
              onToggleDarkMode={toggleDarkMode}
            />

            {/* Alert */}
            {alert && (
              <div className="alert">
                {alert}
                <button onClick={hideAlert} className="alert-close">×</button>
              </div>
            )}

            {/* Patient Information Section - Always visible first */}
            <div style={{ padding: '20px' }}>
              <PatientInfo
                key={patientInfoKey}
                patientInfo={patientInfo}
                setPatientInfo={setPatientInfo}
                isExistingPatient={isExistingPatient}
                tenantId={user?.tenantId}
              />

              <PatientConstantes
                constantes={patientConstantes}
                setConstantes={setPatientConstantes}
              />
            </div>

            {/* Audio Visualization - Only show when patient info is filled */}
            {patientInfo.nom && patientInfo.prenom ? (
              <>
                <AudioVisualization
                  isRecording={isRecording}
                  isPaused={isPaused}
                />

                {/* Generate Report Button */}
                <div style={{ padding: '0 20px 20px 20px' }}>
                  {finalText.length > 100 && (
                    <button
                      className="btn-primary"
                      onClick={async () => {
                        // Stop recording before generating report
                        if (isRecording) {
                          console.log('🛑 Stopping recording and waiting for audio blob...');
                          const audioBlob = await stopRecording();
                          if (audioBlob) {
                            console.log('✅ Audio blob ready after stopping:', audioBlob.size, 'bytes');
                          } else {
                            console.warn('⚠️ No audio blob after stopping recording');
                          }
                        }
                        generateReport(showLoadingScreen, updateLoadingProgress, hideLoadingScreen, navigateToEditReport, patientConstantes);
                      }}
                      style={{
                        marginTop: '24px',
                        width: '100%',
                        padding: '16px',
                        fontSize: '16px',
                        fontWeight: '600'
                      }}
                    >
                      Generate medical report
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div style={{
                padding: '32px 20px',
                textAlign: 'center',
                background: '#f9fafb',
                margin: '20px',
                borderRadius: '12px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>👤</div>
                <h3 style={{ color: '#111827', margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600' }}>
                  Patient information required
                </h3>
                <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>
                  Please fill in at least the patient's first and last name to start the recording session
                </p>
              </div>
            )}

            {/* Info Panel - formerly ControlsPanel, now just shows helpful info */}
            <ControlsPanel />
          </div>
        </>
      )}
    </div>
  );
}

export default App;